<template>
	<section class="empty-state">
		<div class="page-header">
			<h1>{{ $t("nav.activity") }}</h1>
			<p>{{ $t("activityPage.emptyState.title") }}</p>
		</div>

		<hr class="divider" />

		<div class="content">
			<span class="notification-icon" aria-hidden="true">
				<span class="icon notification"></span>
			</span>

			<div class="copy">
				<h2>{{ $t("activityPage.emptyState.title") }}</h2>
				<p>{{ $t("activityPage.emptyState.description") }}</p>
			</div>

			<div class="actions">
				<Button class="button" variant="primary" size="l" @click="$emit('enable-notifications')">
					<span class="icon button-icon notification" aria-hidden="true"></span>
					{{ $t("activityPage.emptyState.enableNotifications") }}
				</Button>
				<Button class="button" variant="tertiary" size="l" @click="$emit('create-search')">
					<span class="icon button-icon saved-search" aria-hidden="true"></span>
					{{ $t("activityPage.emptyState.createSearch") }}
				</Button>
			</div>

			<div class="hint">
				<span class="icon hint-icon saved-search" aria-hidden="true"></span>
				<p>{{ $t("activityPage.emptyState.hint") }}</p>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import Button from "@/components/ui/Button.vue"

defineEmits<{
	"enable-notifications": []
	"create-search": []
}>()
</script>

<style scoped>
.empty-state {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);
	width: 100%;
	height: 100%;
	padding: var(--spacing-xl);
	box-sizing: border-box;
	font-family: var(--font-family-primary);

	.page-header {
		h1 {
			margin: 0;
			color: var(--neutral-900);
			font-size: var(--font-size-xl);
			font-weight: var(--font-weight-semibold);
		}

		p {
			margin: var(--spacing-xs) 0 0;
			color: var(--neutral-600);
			font-size: var(--font-size-small);
		}
	}

	.divider {
		margin: 0 calc(var(--spacing-xl) * -1);
		border: 0;
		border-top: var(--border-width-thin) solid var(--neutral-200);
	}

	.content {
		display: flex;
		flex: 1;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		min-height: 0;
		margin: calc(var(--spacing-lg) * -1) calc(var(--spacing-xl) * -1) calc(var(--spacing-xl) * -1);
		padding: var(--spacing-xl);
		box-sizing: border-box;
	}

	.icon {
		display: inline-block;
		background-color: currentColor;
		mask-position: center;
		mask-repeat: no-repeat;
		mask-size: contain;

		&.notification {
			mask-image: url("/icons/activity-bell.svg");
		}

		&.saved-search {
			mask-image: url("/icons/activity-saved-search.svg");
		}
	}

	.notification-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: var(--space-64);
		height: var(--space-64);
		border-radius: var(--border-radius-full);
		background-color: var(--accent-highlight-light);
		color: var(--primary-green);

		.icon {
			width: var(--size-icon-xl);
			height: var(--size-icon-xl);
		}
	}

	.copy {
		width: min(420px, 100%);
		margin-top: var(--space-20);
		text-align: center;

		h2 {
			margin: 0;
			color: var(--neutral-900);
			font-size: var(--font-size-large);
			font-weight: var(--font-weight-semibold);
			line-height: var(--line-height-tight);
		}

		p {
			margin: var(--space-14) 0 0;
			color: var(--neutral-600);
			font-size: var(--font-size-base);
			line-height: var(--line-height-normal);
		}
	}

	.actions {
		display: flex;
		gap: var(--spacing-sm);
		margin-top: var(--spacing-lg);

		.button {
			gap: var(--spacing-sm);
		}

		.button-icon {
			width: var(--size-icon-md);
			height: var(--size-icon-md);
		}

		@media (max-width: 768px) {
			flex-direction: column;
			width: 100%;

			.button {
				width: 100%;
			}
		}
	}

	.hint {
		display: flex;
		align-items: flex-start;
		gap: var(--space-12);
		width: min(420px, 100%);
		margin-top: var(--spacing-lg);
		padding: var(--spacing-md);
		box-sizing: border-box;
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-lg);
		background-color: var(--neutral-50);
		color: var(--neutral-400);

		.hint-icon {
			flex: none;
			width: var(--size-icon-md);
			height: var(--size-icon-md);
		}

		p {
			margin: 0;
			font-size: var(--font-size-small);
			line-height: var(--line-height-normal);
		}
	}
}
</style>
